import { useState, useEffect } from "react";
import { useDebounced } from "@/utils";

export function useRecorderSettings() {
  const [speed, setSpeed] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderSpeed");
    return stored ? parseInt(stored, 10) : 15;
  });
  const [textSize, setTextSize] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderTextSize");
    return stored ? parseInt(stored, 10) : 24;
  });
  const [aspectRatio, setAspectRatio] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderAspectRatio");
    return stored ? parseFloat(stored) : 9 / 16;
  });
  const [bgType, setBgType] = useState<"none" | "blur" | "gradient" | "image">(() => {
    const stored = localStorage.getItem("scRecorderBgType");
    return (stored as any) || "none";
  });
  const [bgBlur, setBgBlur] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderBgBlur");
    return stored ? parseInt(stored, 10) : 8;
  });
  const [bgColors, setBgColors] = useState<string[]>(() => {
    const stored = localStorage.getItem("scRecorderBgColors");
    try {
      return stored ? JSON.parse(stored) : ["#00FF00"];
    } catch {
      return ["#00FF00"];
    }
  });
  const [bgImage, setBgImage] = useState<string | null>(() => {
    return localStorage.getItem("scRecorderBgImage");
  });
  const [bgLogo, setBgLogo] = useState<string | null>(() => {
    return localStorage.getItem("scRecorderBgLogo");
  });
  const [bgLogoStyle, setBgLogoStyle] = useState<"none" | "watermark" | "backdrop" | "repeat">(() => {
    return (localStorage.getItem("scRecorderBgLogoStyle") as any) || "none";
  });

  const [hasPersistedSettings, setHasPersistedSettings] = useState(() => {
    const keys = [
      "scRecorderSpeed",
      "scRecorderTextSize",
      "scRecorderAspectRatio",
      "scRecorderBgType",
      "scRecorderBgBlur",
      "scRecorderBgColors",
      "scRecorderBgImage",
      "scRecorderBgLogo",
      "scRecorderBgLogoStyle",
      "scRecorderPreferredCamera",
      "scRecorderPreferredMic",
    ];
    return keys.some(key => localStorage.getItem(key) !== null);
  });

  const debouncedSpeed = useDebounced(speed, 500);
  const debouncedTextSize = useDebounced(textSize, 500);
  const debouncedBgBlur = useDebounced(bgBlur, 500);
  const debouncedBgColors = useDebounced(bgColors, 500);

  // Persist speed and text size settings
  useEffect(() => {
    localStorage.setItem("scRecorderSpeed", debouncedSpeed.toString());
    setHasPersistedSettings(true);
  }, [debouncedSpeed]);

  useEffect(() => {
    localStorage.setItem("scRecorderTextSize", debouncedTextSize.toString());
    setHasPersistedSettings(true);
  }, [debouncedTextSize]);

  // Persist background settings
  useEffect(() => {
    localStorage.setItem("scRecorderAspectRatio", aspectRatio.toString());
    setHasPersistedSettings(true);
  }, [aspectRatio]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgType", bgType);
    setHasPersistedSettings(true);
  }, [bgType]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgBlur", debouncedBgBlur.toString());
    setHasPersistedSettings(true);
  }, [debouncedBgBlur]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgColors", JSON.stringify(debouncedBgColors));
    setHasPersistedSettings(true);
  }, [debouncedBgColors]);

  useEffect(() => {
    if (bgImage) {
      localStorage.setItem("scRecorderBgImage", bgImage);
    } else {
      localStorage.removeItem("scRecorderBgImage");
    }
    setHasPersistedSettings(true);
  }, [bgImage]);

  useEffect(() => {
    if (bgLogo) {
      localStorage.setItem("scRecorderBgLogo", bgLogo);
    } else {
      localStorage.removeItem("scRecorderBgLogo");
    }
    setHasPersistedSettings(true);
  }, [bgLogo]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgLogoStyle", bgLogoStyle);
    setHasPersistedSettings(true);
  }, [bgLogoStyle]);

  return {
    speed,
    setSpeed,
    textSize,
    setTextSize,
    aspectRatio,
    setAspectRatio,
    bgType,
    setBgType,
    bgBlur,
    setBgBlur,
    bgColors,
    setBgColors,
    bgImage,
    setBgImage,
    bgLogo,
    setBgLogo,
    bgLogoStyle,
    setBgLogoStyle,
    hasPersistedSettings,
    setHasPersistedSettings,
    debouncedBgBlur,
    debouncedBgColors,
  };
}
